import { Injectable, OnDestroy } from '@angular/core';
import { DeviceType } from 'app.config';
import { Wildcard } from 'extensions/oy/pipes/translate.pipe';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ApiCmsTranslations } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-translations/api-cms-translations.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { Observable, of, Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { DeviceInfoService } from '../device-info/device-info.service';

@Injectable()
export class TranslateService implements OnDestroy {

  private translations?: Record<string, string>;
  private readonly clearSubscriptions$: Subject<void> = new Subject<void>();
  private lastDeviceType: DeviceType = DeviceType.DESKTOP;

  constructor(
    private readonly contextService: ContextService,
    private readonly deviceInfoService: DeviceInfoService,
  ) {
    this.deviceInfoService.breakpointState.pipe(
      takeUntil(this.clearSubscriptions$),
    ).subscribe((activedDevice) => {
      this.lastDeviceType = activedDevice;
    });
  }

  public ngOnDestroy(): void {
    this.clearSubscriptions$.next();
    this.clearSubscriptions$.complete();
  }

  /**
   *  Translate key by specific key component
   *
   * @param key translation key
   * @param component specificic component in order to priorize translations
   * @param wildcards wildcards used to substitute passed values
   * @returns Observable con el texto traducido
   */
  public translate(key: string, component: string = 'COMMONS', ...wildcards: Wildcard[]): Observable<string> {
    if (isDefined(this.translations)) {
      return of(this.getTranslation(this.translations, component, key, wildcards));
    }
    return this.contextService.getContext$().pipe(
      tap((context: EcommerceContext) => this.translations = context.store.details.translations),
      map((context: EcommerceContext) => this.getTranslation(context.store.details.translations, component, key, wildcards)),
      take(1)
    );
  }

  /**
   *  PRIORIDADES A LA HORA DE BUSCAR TRADUS:
   *
   *  1. COMPONENT_KEY_DEVICE
   *  2. COMPONENT_KEY
   *  3. COMMONS_KEY_DEVICE
   *  4. COMMONS_KEY
   *
   * @param translations Translation list of keys
   * @param component specificic component in order to priorize translations
   * @param key translation key
   * @param wildcards wildcards used on SEO
   */
  public getTranslation(translations: ApiCmsTranslations | undefined, component: string, key: string, wildcards: Wildcard[]) {
    let translation = `${key}`;

    // If translations from CMS have arrived...
    if (isDefined(translations)) {
      let device;
      if (this.lastDeviceType === DeviceType.MOBILE) {
        device = 'MOBILE';
      } else if (this.lastDeviceType === DeviceType.TABLET) {
        device = 'TABLET';
      }

      // FOLLOWING TRANSLATIONS PRIORITIES...
      if (component && device && translations[`${component}_${key}_${device}`]) {
        // 1. COMPONENT_KEY_DEVICE
        translation = translations[`${component}_${key}_${device}`];
      } else if (component && translations[`${component}_${key}`]) {
        // 2. COMPONENT_KEY
        translation = translations[`${component}_${key}`];
      } else if (device && translations[`COMMONS_${key}_${device}`]) {
        // 3. COMMONS_KEY_DEVICE
        translation = translations[`COMMONS_${key}_${device}`];
      } else if (translations[`COMMONS_${key}`]) {
        // 4. COMMONS_KEY
        translation = translations[`COMMONS_${key}`];
      }
    }

    return this.replaceWildcards(translation, wildcards);
  }

  /**
   *  Replace wild cards
   *
   * @param translation translated text
   * @param wildcards wildcards
   * @returns translate text with replaced wilcards
   */
  private replaceWildcards(translation: string, wildcards: Wildcard[]): string {
    return translation.replace(/{([0-9]+)}/g, (wildcard, $1: number) => {
      return (isDefined(wildcards[$1]) ? wildcards[$1] : wildcard).toString();
    });
  }

}
